/**
 * Product descriptions returned by /api/v2/descriptions/:lang.
 *
 * We read them from /product-integrations.json (written at build time by
 * scripts/build.ts), so the same file backs:
 *   - the response from the descriptions API,
 *   - uiv2's federation runtime when it looks up `/ui/<shortname>/remoteEntry.js`,
 *   - the healthcheck/services endpoint's "which products are up".
 *
 * Falls back to an empty list when no integrations are present — useful
 * for sanity-running uiv2 alone before integrations are built.
 */

export interface ProductManifest {
  shortname: string;
  title: Record<string, string>;
  description: Record<string, string>;
  icon: string | null;
  docs: string | null;
  component: { type: "component" | "markdown" | "link"; ref: string };
  preview?: string;
  hasRemoteEntry?: boolean;
}

interface ProductIntegrationsJson {
  products: ProductManifest[];
  buildTime: string;
}

interface ProductDescription {
  shortname: string;
  title: string;
  icon: string | null;
  description: string;
  language: string;
  docs: string | null;
  component: { type: "component" | "markdown" | "link"; ref: string };
}

let _cached: ProductIntegrationsJson | null = null;

async function loadProductIntegrations(): Promise<ProductIntegrationsJson> {
  if (_cached) return _cached;
  try {
    const res = await fetch("/product-integrations.json");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    _cached = await res.json();
    return _cached!;
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      "[mock] /product-integrations.json missing — returning empty product list. " +
        "This is expected if scripts/build.ts hasn't run yet.",
    );
    _cached = { products: [], buildTime: new Date().toISOString() };
    return _cached;
  }
}

function manifestToDescription(
  manifest: ProductManifest,
  language: string,
): ProductDescription {
  return {
    shortname: manifest.shortname,
    title:
      manifest.title[language] || manifest.title["en"] || manifest.shortname,
    icon: manifest.icon,
    description:
      manifest.description[language] || manifest.description["en"] || "",
    language,
    docs: manifest.docs,
    component: manifest.component,
  };
}

export async function getMockProducts(
  language: string,
): Promise<ProductDescription[]> {
  const data = await loadProductIntegrations();
  return data.products.map((p) => manifestToDescription(p, language));
}

export async function getMockProductShortnames(): Promise<string[]> {
  const data = await loadProductIntegrations();
  return data.products.map((p) => p.shortname);
}
